/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : ChangePasswordModal.tsx
// 설명   : 비밀번호 변경 팝업(공용) - LoginPage(최초/강제 비밀번호변경)와 mainFrame(사용자영역에서
//         자발적으로 변경) 양쪽에서 재사용함.
//         - currentPassword를 이미 알고 있는 경우(로그인 직후 강제변경 흐름 - 방금 입력한 로그인
//           비밀번호를 그대로 씀) 재입력을 요구하지 않고, 없는 경우(사용자영역에서 자발적으로 여는
//           경우)에는 "현재 비밀번호" 입력창을 추가로 보여줌.
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import '../styles/loginPage.css';
import { useCommonTranslation } from '../translations';
import { getMsgText } from '../utils/commonUtils';

export interface ChangePasswordModalProps {
  open: boolean;
  userId: string;
  currentPassword?: string; // 넘기면 "현재 비밀번호" 입력창을 숨기고 이 값을 그대로 oldPassword로 사용
  title?: string;
  description?: string;
  submitLabel?: string;
  onClose: () => void;
  // 변경 성공 시 호출(모달을 닫는 것은 호출부 책임) - apiChangePassword 응답의 rsResults를 그대로 넘김.
  // 자발적 변경(이미 로그인됨)은 { token } 형태이고, 로그인 전 강제변경은 2차인증 미등록/미완료 상태면
  // { mfaRequired, mfaStage, pendingToken, ... } 형태로 올 수 있음(LoginPage가 이 값으로 판단).
  onSuccess: (rsResults: { token?: string; mfaRequired?: boolean; mfaStage?: 'enroll' | 'verify'; pendingToken?: string; hasEmail?: boolean; method?: string }) => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open, userId, currentPassword, title, description, submitLabel, onClose, onSuccess,
}) => {
  const { ddKey, language } = useCommonTranslation();
  const [curPwd, setCurPwd] = useState('');
  const [newPwd1, setNewPwd1] = useState('');
  const [newPwd2, setNewPwd2] = useState('');
  const [error, setError] = useState('');
  const [changing, setChanging] = useState(false);

  const needsCurrentPwd = currentPassword === undefined;

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (needsCurrentPwd && !curPwd) {
      setError(ddKey('msgCurrentPwdRequired') || '현재 비밀번호를 입력하세요.');
      return;
    }
    if (!newPwd1 || !newPwd2) {
      setError(ddKey('msgNewPwdRequired') || '새 비밀번호를 입력하세요.');
      return;
    }
    if (newPwd1 !== newPwd2) {
      setError(ddKey('msgNewPwdMismatch') || '새 비밀번호가 서로 일치하지 않습니다.');
      return;
    }
    setChanging(true);
    setError('');
    try {
      const res = await axios.post(`${API_BASE_URL}/com/apiChangePassword`, {
        langCd: language,
        userId,
        oldPassword: currentPassword ?? curPwd,
        newPassword: newPwd1,
      });
      const rsResults = res.data?.rsResults;
      if (rsResults?.success) {
        setCurPwd(''); setNewPwd1(''); setNewPwd2('');
        onSuccess(rsResults || {});
      } else {
        setError(rsResults?.message || ddKey('msgPwdChangeFailed') || '비밀번호 변경에 실패했습니다.');
      }
    } catch (err: any) {
      setError(getMsgText(err, ddKey('msgPwdChangeError') || '비밀번호 변경 중 오류가 발생했습니다.'));
    } finally {
      setChanging(false);
    }
  };

  const handleCancel = () => {
    setCurPwd(''); setNewPwd1(''); setNewPwd2(''); setError('');
    onClose();
  };

  return (
    <div className="pwd-change-overlay">
      <div className="pwd-change-card">
        <h3 className="login-title">{title || ddKey('lblPwdChangeTitle') || '비밀번호 변경'}</h3>
        <p className="pwd-change-desc">
          {description || ddKey('msgPwdChangeDesc') || '새 비밀번호를 입력하세요.'}
        </p>
        <form className="login-form" onSubmit={handleSubmit}>
          {needsCurrentPwd && (
            <div className="form-group">
              <label htmlFor="curPwd" className="form-label">{ddKey('lblCurrentPassword') || '현재 비밀번호'}</label>
              <input
                id="curPwd"
                type="password"
                required
                className="form-input"
                value={curPwd}
                onChange={(e) => setCurPwd(e.target.value)}
                disabled={changing}
                autoFocus
              />
            </div>
          )}
          <div className="form-group">
            <label htmlFor="newPwd1" className="form-label">{ddKey('newPassword') || '새 비밀번호'}</label>
            <input
              id="newPwd1"
              type="password"
              required
              className="form-input"
              value={newPwd1}
              onChange={(e) => setNewPwd1(e.target.value)}
              disabled={changing}
              autoFocus={!needsCurrentPwd}
            />
          </div>
          <div className="form-group">
            <label htmlFor="newPwd2" className="form-label">{ddKey('newPasswordConfirm') || '새 비밀번호 확인'}</label>
            <input
              id="newPwd2"
              type="password"
              required
              className="form-input"
              value={newPwd2}
              onChange={(e) => setNewPwd2(e.target.value)}
              disabled={changing}
            />
          </div>
          <p className="pwd-change-hint">
            {ddKey('msgPwdPolicyHint') || '최소 10자 이상, 영문 대/소문자·숫자·특수문자 중 3종류 이상 조합, 아이디 포함 불가'}
          </p>
          {error && <p className="pwd-change-error">{error}</p>}
          <div className="form-group pwd-change-actions">
            <button type="submit" className={`login-btn ${changing ? 'loading' : ''}`} disabled={changing}>
              {changing ? (ddKey('loading') || '처리중...') : (submitLabel || ddKey('btnChangePassword') || '비밀번호 변경')}
            </button>
            <button
              type="button"
              className="pwd-change-cancel-btn"
              disabled={changing}
              onClick={handleCancel}
            >
              {ddKey('btnCancel') || '취소'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
